import { formatAmount, formatDateWithYear } from "../hooks/dataHandler";
import { sanitizeText } from "../helpers";
import { resolvePdfFontName } from "./fontUtils";
import {
  getCrossYAApportionmentContext,
  getCrossYABasisPeriods,
} from "../../components/MainTaxComputation/utils/precedingCalculation";

const fm = "helvetica";
const COMPANY_NAME_FONT_SIZE = 14;
const COMPANY_NAME_MIN_FONT_SIZE = 10;
const COMPANY_NAME_LINE_HEIGHT = 5;

export const lineBreakText = (content, cellPadding = { top: 0, bottom: 0 }) => {
  return {
    content,
    styles: {
      overflow: "lineBreak",
      cellPadding: cellPadding,
    },
  };
};

export const boldAutoColumn = (content, lineBreak = false) => {
  return {
    content,
    styles: {
      fontStyle: "bold",
      overflow: lineBreak && "lineBreak",
    },
  };
};

export const italicAutoColumn = (content, lineBreak = true, fs = 8) => {
  return {
    content,
    styles: {
      fontStyle: "italic",
      overflow: lineBreak && "lineBreak",
      fontSize: fs,
    },
  };
};

export const autoTextColorNoBreak = (content, color = [255, 0, 0]) => {
  return {
    content,
    styles: {
      textColor: color,
    },
  };
};

export const autoTextColor = (
  content,
  color = [255, 0, 0],
  lineBreak = true,
  cellPadding = { top: 3, bottom: 0 }
) => {
  if (!content || content === "") return "";
  return {
    content,
    styles: {
      cellPadding: cellPadding,
      textColor: color,
      overflow: lineBreak && "lineBreak",
    },
  };
};

export const redText = (content, color = [255, 0, 0], lineBreak = true) => {
  if (!content || content === "") return "";
  return {
    content,
    styles: {
      textColor: color,
      overflow: lineBreak && "lineBreak",
    },
  };
};

export const beautyAutoLine = (
  data,
  doc,
  condition,
  doubleLineCondition = false,
  customLineWidth = null,
  ignoreCondition = false
) => {
  if (data.row.section === "body" && condition) {
    let cellCenterX = data.cell.x + data.cell.width / 2;
    let y = data.cell.y + data.cell.height;

    let lineWidth;
    if (customLineWidth !== null) {
      lineWidth = customLineWidth;
    } else {
      const text = Array.isArray(data.cell.text)
        ? data.cell.text.join(" ")
        : data.cell.text || "";
      const maxWidth = data.cell.width * 0.95;
      lineWidth = maxWidth;
    }

    let xStart = cellCenterX - lineWidth / 2;
    let xEnd = cellCenterX + lineWidth / 2;

    doc.setDrawColor(0);
    doc.setLineWidth(0.1);
    if (!ignoreCondition) doc.line(xStart, y, xEnd, y);

    if (doubleLineCondition) {
      let secondLineY = y + 1.2;
      doc.line(xStart, secondLineY, xEnd, secondLineY);
    }
  }
};

export const autoLine = (content) => {
  return {
    content,
    styles: {
      lineWidth: {
        top: 0,
        left: 0,
        right: 0,
        bottom: 0.1,
      },
      lineColor: [0, 0, 0],
    },
  };
};

export const checkPageBreak = (
  doc,
  requiredSpace,
  startY,
  initY,
  pageHeight,
  margin = 20
) => {
  if (startY + requiredSpace > pageHeight - margin) {
    doc.addPage();
    startY = initY;
    return initY;
  }

  return startY;
};

export const addBoldText = (doc, text, x, y, fontSize = 10, align = "left") => {
  doc.setFontSize(fontSize);
  doc.setFont(resolvePdfFontName(doc, text, fm), "bold");
  doc.text(text, x, y, { align: align });
  doc.setFont(fm, "normal");
};

export const addItalicText = (
  doc,
  text,
  x,
  y,
  fontSize = 10,
  align = "left"
) => {
  doc.setFontSize(fontSize);
  doc.setFont(resolvePdfFontName(doc, text, fm), "italic");
  doc.text(text, x, y, { align: align });
  doc.setFont(fm, "normal");
};

export const addText = (doc, text, x, y, fontSize = 10, align = "left") => {
  if (align == "right") x = x * 1.05;

  doc.setFontSize(fontSize);
  doc.setFont(resolvePdfFontName(doc, text, fm), "normal");
  doc.text(text, x, y, { align: align });
  doc.setFont(fm, "normal");
};

export const drawUnderline = (doc, x, y, width) => {
  doc.line(x - 12, y + 2, x + width - 12, y + 2);
};

export const drawDoubleUnderline = (doc, x, y, width) => {
  y += 2;
  doc.line(x - 12, y, x + width - 12, y);
  y += 1;
  doc.line(x - 12, y, x + width - 12, y);
  y += 10;
};

export const drawCheckbox = (doc, x, y, needTick) => {
  doc.rect(x, y - 2, 5, 5);
  if (needTick) {
    doc.setLineWidth(0.5);
    doc.line(x + 1, y, x + 2, y + 3);
    doc.line(x + 2, y + 3, x + 4, y - 2);
    doc.setLineWidth(0);
  }
};

export const drawNotes = (
  doc,
  leftX,
  rightX,
  y,
  initY,
  lineHeight,
  pageHeight,
  comments,
  isReview = false
) => {
  y += 5;
  if (y + lineHeight <= pageHeight - 20) {
    addBoldText(
      doc,
      isReview ? "Internal Review Notes" : "Notes/Comments",
      leftX,
      y
    );
    y += 2;
    doc.line(leftX, y + 2, rightX, y + 2);
    y += 7;
  }
  y += 2;

  const commentLines = doc.splitTextToSize(comments, 190);

  commentLines.forEach((line) => {
    if (y + lineHeight > pageHeight - 10) {
      doc.addPage();
      y = initY;
      addBoldText(doc, "Notes/Comments", leftX, y);
      doc.line(leftX, y + 2, rightX, y + 2);
      y += lineHeight;
    }
    addText(doc, line, leftX, y);
    y += lineHeight;
  });

  return y;
};

export const getCompanyNameHeaderLayout = (doc, company, leftX) => {
  const companyName = sanitizeText(company?.name);
  const paragraphs = companyName.split(/\r?\n/);
  const maxWidth = doc.internal.pageSize.getWidth() - leftX * 2;
  const previousFont = doc.getFont();
  const previousFontSize = doc.getFontSize();

  // Measure with the same font used to render, including the CJK fallback.
  doc.setFont(resolvePdfFontName(doc, companyName, fm), "bold");
  doc.setFontSize(COMPANY_NAME_FONT_SIZE);
  const textWidth = Math.max(
    ...paragraphs.map((line) => doc.getTextWidth(line))
  );
  const fontSize =
    textWidth > maxWidth
      ? Math.max(
          COMPANY_NAME_MIN_FONT_SIZE,
          Math.floor((COMPANY_NAME_FONT_SIZE * maxWidth * 100) / textWidth) /
            100
        )
      : COMPANY_NAME_FONT_SIZE;

  doc.setFontSize(fontSize);
  // Keep fitting lines intact; splitting words uses slightly different kerning.
  const lines = paragraphs.flatMap((line) =>
    doc.getTextWidth(line) <= maxWidth
      ? [line]
      : doc.splitTextToSize(line, maxWidth)
  );
  doc.setFont(previousFont.fontName, previousFont.fontStyle);
  doc.setFontSize(previousFontSize);

  return {
    lines,
    fontSize,
    extraHeight: (lines.length - 1) * COMPANY_NAME_LINE_HEIGHT,
  };
};

const addCompanyNameHeader = (doc, company, leftX, startY) => {
  const { lines, fontSize, extraHeight } = getCompanyNameHeaderLayout(
    doc,
    company,
    leftX
  );
  lines.forEach((line, index) => {
    addBoldText(
      doc,
      line,
      leftX,
      startY + index * COMPANY_NAME_LINE_HEIGHT,
      fontSize
    );
  });

  return startY + COMPANY_NAME_LINE_HEIGHT + extraHeight;
};

export const buildCompanyHeader = (
  company,
  headerName,
  doc,
  leftX,
  startY,
  fs
) => {
  const taxRef = sanitizeText(company?.uen);
  startY = addCompanyNameHeader(doc, company, leftX, startY);

  addText(doc, `Tax Reference Number: ${taxRef}`, leftX, startY, fs);
  startY += 5;
  addText(doc, `Tax Computation`, leftX, startY, fs);
  startY += 5;
  addText(
    doc,
    `Year of Assessment: ${company.yearOfAssessment}`,
    leftX,
    startY,
    fs
  );
  startY += 5;
  addText(
    doc,
    `Basis Period: ${formatDateWithYear(
      company.financialYearStart
    )} to ${formatDateWithYear(company.financialYearEnd)}`,
    leftX,
    startY,
    fs
  );
  startY += 10;

  addBoldText(doc, `${headerName}`, leftX, startY, 12);
  startY += 10;

  return startY;
};

export const hasCrossYABasisHeader = (company) => {
  const context = getCrossYAApportionmentContext(
    company,
    company?.taxPeriodType === "cross_ya"
  );
  return (
    Boolean(context) &&
    Number(context.precedingDays) > 0 &&
    Number(context.totalDays) > Number(context.precedingDays)
  );
};

const formatApportionmentBasis = (basis, fallbackDays, totalDays) => {
  if (Number.isFinite(Number(basis))) return Number(basis).toFixed(4);
  if (!fallbackDays || !totalDays) return "0";

  return (Number(fallbackDays) / Number(totalDays)).toFixed(4);
};

const buildCrossYABasisRows = (company) => {
  const periods = getCrossYABasisPeriods(
    company,
    company?.taxPeriodType === "cross_ya"
  );
  const context = periods?.context;
  if (
    !periods ||
    Number(context.precedingDays) <= 0 ||
    Number(context.totalDays) <= Number(context.precedingDays)
  ) {
    return null;
  }

  const yearOfAssessment = Number(company.yearOfAssessment);
  const currentDays =
    context.currentDays ?? context.totalDays - context.precedingDays;

  return [
    {
      label: `Preceding YA ${yearOfAssessment - 1}`,
      start: periods.preceding.financialYearStart,
      end: periods.preceding.financialYearEnd,
      days: context.precedingDays,
      basis: formatApportionmentBasis(
        context.precedingBasis,
        context.precedingDays,
        context.totalDays
      ),
    },
    {
      label: `Current YA ${yearOfAssessment}`,
      start: periods.current.financialYearStart,
      end: periods.current.financialYearEnd,
      days: currentDays,
      basis: formatApportionmentBasis(
        context.currentBasis,
        currentDays,
        context.totalDays
      ),
    },
  ];
};

export const buildScheduleCompanyHeader = (
  company,
  schedule,
  doc,
  leftX,
  rightX,
  startY,
  fs
) => {
  const taxRef = sanitizeText(company?.uen);

  startY = addCompanyNameHeader(doc, company, leftX, startY);

  addText(doc, `Tax Reference Number: ${taxRef}`, leftX, startY, fs);
  startY += 5;

  addText(
    doc,
    sanitizeText(schedule?.headerLabel || "Tax Computation"),
    leftX,
    startY,
    fs
  );
  startY += 5;

  const crossYABasisRows = buildCrossYABasisRows(company);
  if (crossYABasisRows) {
    addText(
      doc,
      `Year of Assessment: ${company.yearOfAssessment - 1} / ${
        company.yearOfAssessment
      }`,
      leftX,
      startY,
      fs
    );
    startY += 5;

    crossYABasisRows.forEach((row) => {
      addText(
        doc,
        `Basis Period of ${row.label}: ${formatDateWithYear(
          row.start
        )} to ${formatDateWithYear(row.end)} (${row.days} days; Basis: ${row.basis})`,
        leftX,
        startY,
        fs
      );
      startY += 5;
    });

    startY += 5;
  } else {
    addText(
      doc,
      `Year of Assessment: ${company.yearOfAssessment}`,
      leftX,
      startY,
      fs
    );
    startY += 5;

    addText(
      doc,
      `Basis Period: ${formatDateWithYear(
        company.financialYearStart
      )} to ${formatDateWithYear(company.financialYearEnd)}`,
      leftX,
      startY,
      fs
    );
    startY += 10;
  }

  addBoldText(doc, sanitizeText(schedule?.name), leftX, startY, 12);

  if (schedule.scheduleNo)
    addBoldText(doc, `Schedule [${schedule.scheduleNo}]`, rightX, startY, fs);

  if (schedule.subTitle)
    addBoldText(doc, `${schedule.subTitle}`, rightX, startY, fs);

  // Add currency rate only when currency is not SGD
  if (
    !(schedule.scheduleNo || schedule.subTitle) &&
    company.currency &&
    company.currency !== "SGD"
  ) {
    addBoldText(
      doc,
      `Exchange Rate: 1 ${company.currency} = ${company.manual_currency_rate} SGD`,
      rightX - 35,
      startY,
      fs
    );
  }

  return startY;
};

export const buildNotesWithCurrencyRate = (company, notes) => {
  if (!company) return notes;

  if (!company.currency || company.currency === "SGD") return notes;

  if (!notes) notes = "";
  if (notes !== "") notes += "\n";

  const masRate = company.currencyRate;
  const manualRate = company.manual_currency_rate;
  const isManualOverride =
    company.masAvailable && manualRate && manualRate !== masRate;

  let currencyNote = "";

  if (!company.masAvailable) {
    currencyNote =
      `MAS does not provide exchange rate for ${company.currency}.\n` +
      `Manual exchange rate applied: ${company.currency} 1 : SGD ${manualRate}\n`;
  } else if (isManualOverride) {
    currencyNote =
      `Average monthly exchange rate (MAS): ${company.currency} 1 : SGD ${masRate}\n` +
      `Manual exchange rate applied: ${company.currency} 1 : SGD ${manualRate}\n`;
  } else {
    currencyNote = `Average monthly exchange rate obtained from MAS: ${company.currency} 1 : SGD ${masRate}\n`;
  }

  return notes + currencyNote;
};

export const buildCalculatorNotes = (reviewNotes, data, needRound = true) => {
  if (!data) return reviewNotes;

  const historyAmountRows = data.filter(
    (row) => row.historyAmount && row.historyAmount.length > 1
  );

  if (historyAmountRows && historyAmountRows.length > 0) {
    if (reviewNotes !== "") reviewNotes += "\n\n";
    reviewNotes += "[Amount Calculation]";
    historyAmountRows.forEach((row) => {
      // Show the history breakdown for each row
      const amountBreakdown = row.historyAmount
        .map((amount) => formatAmount(amount, needRound))
        .join(" + ");
      reviewNotes += `\n${
        row.name || row.description || row.approvedIPC
      } = ${formatAmount(row.amount, needRound)} =  ${amountBreakdown}`;
    });
  }

  return reviewNotes;
};

export const buildNotes = (
  notes,
  reviewNotes,
  doc,
  startY,
  initY,
  leftX,
  rightX,
  lineHeight,
  pageHeight,
  refreshY,
  forView,
  checkBreak = false
) => {
  if ((notes && notes !== "") || (reviewNotes && reviewNotes !== "")) {
    if (!checkBreak) {
      doc.addPage();
      startY = initY;
    } else {
      startY = checkPageBreak(doc, 40, startY, refreshY, pageHeight);
    }

    if (notes && notes !== "") {
      startY = drawNotes(
        doc,
        leftX,
        rightX,
        startY,
        initY,
        lineHeight,
        pageHeight,
        notes
      );
    }

    doc.setTextColor(255, 0, 0);
    doc.setDrawColor(255, 0, 0);
    if (forView && reviewNotes && reviewNotes !== "") {
      startY = checkPageBreak(doc, 40, startY, refreshY, pageHeight);
      drawNotes(
        doc,
        leftX,
        rightX,
        startY,
        initY,
        lineHeight,
        pageHeight,
        reviewNotes,
        true
      );
    }
  }
};
